import clsx from 'clsx';
import type * as LucideIcons from 'lucide-react';
import type { SVGProps } from 'react';
import { memo } from 'react';
import { useLoadIcon } from '../../common/hooks/use-load-icon';

export type OdinIconName = keyof typeof LucideIcons;

export enum OdinIconSize {
  ExtraExtraSmall,
  ExtraSmall,
  SemiSmall,
  Small,
  Medium,
  Large,
}

export interface OdinIconProps extends Omit<SVGProps<SVGSVGElement>, 'ref' | 'children'> {
  icon: OdinIconName;
  className?: string;
  size?: OdinIconSize;
  /**
   * Stroke width for the icon (1-3 typical). Default is 2.
   */
  strokeWidth?: number;
  spin?: boolean;
  showFocus?: boolean;
}

const OdinIcon = memo(
  ({
    icon,
    size = OdinIconSize.Small,
    className = '',
    strokeWidth = 2,
    spin = false,
    showFocus = true,
    ...props
  }: OdinIconProps) => {
    // useLoadIcon returns the actual Lucide Icon component based on icon name.
    // Lucide components accept LucideProps (size, strokeWidth, className, color, ...SVGProps).
    const Icon = useLoadIcon(icon);
    const iconSize = {
      [OdinIconSize.ExtraExtraSmall]: 10,
      [OdinIconSize.ExtraSmall]: 16,
      [OdinIconSize.SemiSmall]: 18,
      [OdinIconSize.Small]: 20,
      [OdinIconSize.Medium]: 24,
      [OdinIconSize.Large]: 32,
    };
    const iconClassNames = clsx('odin-shrink-0', className, {
      'odin-animate-spin': spin,
      'focus:odin-ring-0 focus:odin-outline-none': showFocus,
    });

    return (
      <Icon
        size={iconSize[size]}
        strokeWidth={strokeWidth}
        className={iconClassNames}
        color="currentColor"
        {...props}
      />
    );
  }
);

OdinIcon.displayName = 'OdinIcon';
export default OdinIcon;
